import React, { useState, useEffect, useContext } from "react";
import { Table, Container, Alert, Form, Row, Col } from "react-bootstrap";
import Preloader from "../../shared/PreLoaders";
import { AuthContext } from "../../Context/AuthContext";
import { logActivity } from "../../utils/LogActivity";
import TableComponent from "../../utils/TableComponent";
import { Authorization } from "../../Context/Authorization";
const ManageLoan = () => {
  const [loans, setLoans] = useState([]);
  const [error, setError] = useState("");
  const [vfcId, setVfcId] = useState("");
  const [loanType, setLoanType] = useState("");
  const [memberId, setMemberId] = useState("");
  const [loanNo, setLoanNo] = useState("");
  const [date, setDate] = useState("");
  const [vfcOptions, setVfcOptions] = useState([]);
  const [loanTypeOptions, setLoanTypeOptions] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [hasFiltered, setHasFiltered] = useState(false);
  const [loading, setLoading] = useState(true);
  const { userId } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    setLoading(true);
    const fetchSelectOptions = async () => {
      try {
        const Config = Authorization();
        const [vfcRes, loanTypeRes, memberRes] = await Promise.all([
          fetch(`${Api}/api/vfcs`, Config),
          fetch(`${Api}/api/loan-types`, Config),
          fetch(`${Api}/api/members`, Config),
        ]);

        const vfcData = await vfcRes.json();
        const loanTypeData = await loanTypeRes.json();
        const memberData = await memberRes.json();
        // Filter VFC options based on access.vfcId
        const filteredVfcOptions = vfcData.vfcs;
        setVfcOptions(filteredVfcOptions);
        // Filter member options, ensuring vfcCode is non-null
        const filteredMemberOptions = memberData.members;
        setMemberOptions(filteredMemberOptions);

        // Set loan type options
        setLoanTypeOptions(loanTypeData.loanTypes);
      } catch (err) {
        setError("Error fetching data");
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSelectOptions();
  }, [Api]);

  const handleFilter = async () => {
    setLoading(true);
    const filterData = {};
    if (vfcId) filterData.vfcId = vfcId;
    if (loanType) filterData.loanType = loanType;
    if (memberId) filterData.memberId = memberId;
    if (loanNo) filterData.loanNo = loanNo;
    if (date) filterData.date = date;

    try {
      const Config = Authorization();
      const response = await fetch(`${Api}/api/loans/filter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...Config.headers,
        },
        body: JSON.stringify(filterData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.data && Array.isArray(data.data)) {
        setLoans(data.data);
        setHasFiltered(true);
      } else {
        setLoans([]);
      }
    } catch (err) {
      setError("No Loans On the selected Field");
      console.error("API Error:", err);
    }
    setLoading(false);
    await logActivity(
      userId,
      "Viewed Loans By Filter",
      "Manage Loan Page",
      Api
    );
  };

  const columns = [
    {
      headerName: "SI.No",
      field: "index",
      valueGetter: (params) => params.node.rowIndex + 1,
    },
    {
      headerName: "Name",
      field: "subLoans",
      valueGetter: (params) => {
        if (params.data.subLoans && params.data.subLoans.length > 0) {
          return params.data.subLoans[0].memberId?.name || "N/A";
        }
        return "N/A";
      },
    },
    {
      headerName: "VFC ID",
      field: "vfcId.vfcName",
    },
    {
      headerName: "Loan No",
      field: "loanNo",
    },
    {
      headerName: "Date",
      field: "date",
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      headerName: "Total Loan Amount",
      field: "amount",
    },
    {
      headerName: "Loan Type",
      field: "loanType.loanName",
    },
  ];

  return (
    <div>
      <Preloader loading={loading} />

      <div className="page-header">
        <h3 className="page-title"> Loans </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Loans
            </li>
          </ol>
        </nav>
      </div>

      <Container>
        <>
          {error && <Alert variant="danger">{error}</Alert>}
          <div className="col-md-12 grid-margin stretch-card">
            <div className="card">
              <div className="card-body">
                <Form>
                  <Row>
                    <Col>
                      <Form.Group controlId="vfcId">
                        <Form.Label>VFC</Form.Label>
                        <div className="input-container">
                          <Form.Control
                            as="select"
                            value={vfcId}
                            onChange={(e) => setVfcId(e.target.value)}
                          >
                            <option value="">All VFCs</option>
                            {vfcOptions.map((vfc) => (
                              <option key={vfc._id} value={vfc._id}>
                                {vfc.vfcName}
                              </option>
                            ))}
                          </Form.Control>
                        </div>
                      </Form.Group>
                    </Col>

                    <Col>
                      <Form.Group controlId="loanType">
                        <Form.Label>Loan Type</Form.Label>
                        <div className="input-container">
                          <Form.Control
                            as="select"
                            value={loanType}
                            onChange={(e) => setLoanType(e.target.value)}
                          >
                            <option value="">All Loan Types</option>
                            {loanTypeOptions.map((type) => (
                              <option key={type._id} value={type._id}>
                                {type.loanName}
                              </option>
                            ))}
                          </Form.Control>
                        </div>
                      </Form.Group>
                    </Col>

                    <Col>
                      <Form.Group controlId="memberId">
                        <Form.Label>Member</Form.Label>
                        <div className="input-container">
                          <Form.Control
                            as="select"
                            value={memberId}
                            onChange={(e) => setMemberId(e.target.value)}
                          >
                            <option value="">All Members</option>
                            {memberOptions.map((member) => (
                              <option key={member._id} value={member._id}>
                                {member.name}
                              </option>
                            ))}
                          </Form.Control>
                        </div>
                      </Form.Group>
                    </Col>

                    <Col>
                      <Form.Group controlId="loanNo">
                        <Form.Label>Loan Number</Form.Label>
                        <div className="input-container">
                          <Form.Control
                            type="text"
                            placeholder="Enter Loan No"
                            value={loanNo}
                            onChange={(e) => setLoanNo(e.target.value)}
                          />
                        </div>
                      </Form.Group>
                    </Col>

                    <Col>
                      <Form.Group controlId="date">
                        <Form.Label>Date</Form.Label>
                        <div className="input-container">
                          <Form.Control
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                          />
                        </div>
                      </Form.Group>
                    </Col>

                    <Col>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-icon-text mt-4"
                        onClick={handleFilter}
                      >
                        <i className="mdi mdi-file-check btn-icon-prepend"></i>
                        Filter
                      </button>
                    </Col>
                  </Row>
                </Form>
              </div>
            </div>
          </div>

          {hasFiltered ? (
            <div className="card">
              <div className="card-body">
                <div className="row icons-list">
                  <div className="Table_data">
                    <TableComponent data={loans} columns={columns} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p>Please apply a filter to view loans.</p>
          )}
        </>
      </Container>
    </div>
  );
};

export default ManageLoan;
